import { Button, Empty } from "antd";
import {
  DeleteOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  HolderOutlined,
} from "@ant-design/icons";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import AIOffBadge from "./AIOffBadge";

/**
 * Pure mapper from a @dnd-kit drag-end event to the (from, to) index pair
 * that onReorderSections expects. Exported for unit testing.
 *
 * Returns null when there is no drop target or the item was dropped on
 * itself (a no-op), so the caller can skip dispatching a reorder.
 */
export const sectionDragEndToReorder = (sections, { active, over }) => {
  if (!over || active.id === over.id) return null;
  const from = sections.findIndex((s) => s.section_id === active.id);
  const to = sections.findIndex((s) => s.section_id === over.id);
  if (from < 0 || to < 0) return null;
  return { from, to };
};

/**
 * A single sortable section card. Uses @dnd-kit's useSortable to wire up the
 * smooth drag transform/transition. The drag listeners live on the grip
 * handle only, so clicking the body of the card still selects the section.
 */
function SortableSectionCard({
  section,
  index,
  isSelected,
  isLast,
  onSelectSection,
  onRemoveSection,
  onReorderSections,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.section_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`border rounded p-3 flex items-start gap-3 cursor-pointer transition ${
        isSelected
          ? "border-blue-500 bg-blue-50"
          : "border-gray-200 bg-white"
      }`}
      onClick={() => onSelectSection?.(index)}
      data-testid={`template-canvas-section-${index}`}
    >
      {/* Drag handle: only this carries the drag listeners so the rest of the
          card stays clickable for selection. */}
      <div className="flex flex-col gap-1 items-center">
        <button
          type="button"
          className="bg-transparent border-0 p-0 leading-none cursor-grab"
          aria-label="Reorder section"
          onClick={(e) => e.stopPropagation()}
          {...attributes}
          {...listeners}
        >
          <HolderOutlined className="text-gray-400" />
        </button>
        <Button
          size="small"
          type="text"
          icon={<ArrowUpOutlined />}
          disabled={index === 0}
          onClick={(e) => {
            e.stopPropagation();
            onReorderSections?.(index, index - 1);
          }}
          aria-label="Move section up"
        />
        <Button
          size="small"
          type="text"
          icon={<ArrowDownOutlined />}
          disabled={isLast}
          onClick={(e) => {
            e.stopPropagation();
            onReorderSections?.(index, index + 1);
          }}
          aria-label="Move section down"
        />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-sm text-gray-900 truncate">
            {section.display_name || section.section_id}
          </h4>
          {section.classification === "clinician_controlled" && <AIOffBadge />}
        </div>
        <div className="text-xs text-gray-500 mt-1 flex items-center gap-3">
          <span>{section.classification}</span>
          <span>·</span>
          <span>{section.style}</span>
          <span>·</span>
          <span>
            AMD: {section.amd_field_name}
            {section.amd_control_id
              ? ` (${section.amd_control_id})`
              : " (not yet provisioned)"}
          </span>
        </div>
      </div>

      <Button
        size="small"
        type="text"
        danger
        icon={<DeleteOutlined />}
        onClick={(e) => {
          e.stopPropagation();
          onRemoveSection?.(index);
        }}
        aria-label="Remove section"
      />
    </li>
  );
}

/**
 * Ordered list of sections in the template being edited.
 *
 * Reorder via:
 *   1. @dnd-kit/sortable drag-and-drop on the grip handle (mouse / touch).
 *   2. Keyboard-accessible Up/Down buttons (WCAG fallback), plus @dnd-kit's
 *      KeyboardSensor for arrow-key dragging from the focused grip handle.
 *
 * The public prop contract is unchanged: every reorder funnels through
 * onReorderSections(from, to), which the reducer uses to re-index `position`
 * contiguously (the fix that avoided a 422 multi-section-save bug).
 */
export default function TemplateCanvas({
  sections = [],
  selectedIndex,
  onSelectSection,
  onRemoveSection,
  onReorderSections,
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    const result = sectionDragEndToReorder(sections, event);
    if (result) {
      onReorderSections?.(result.from, result.to);
    }
  };

  if (!sections.length) {
    return (
      <div className="flex-1 bg-white flex items-center justify-center">
        <Empty
          description={
            <span className="text-gray-500">
              Click a section in the library on the left to add it (or drag it
              here). Remove a section with the trash icon, reorder with the grip
              handle.
            </span>
          }
        />
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={sections.map((s) => s.section_id)}
        strategy={verticalListSortingStrategy}
      >
        <ol className="flex-1 bg-white p-4 overflow-y-auto space-y-2">
          {sections.map((section, index) => (
            <SortableSectionCard
              key={section.section_id}
              section={section}
              index={index}
              isSelected={selectedIndex === index}
              isLast={index === sections.length - 1}
              onSelectSection={onSelectSection}
              onRemoveSection={onRemoveSection}
              onReorderSections={onReorderSections}
            />
          ))}
        </ol>
      </SortableContext>
    </DndContext>
  );
}
